import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { CardModule } from 'primeng/card';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { homeRoute } from './../../core/routing/route-names';
import { ChangePasswordDTO, UserAccountClient } from '../models/models.swagger.generated';
import { ToastService } from '../services/toast.service';
import { passwordValidation } from '../directives/password-validation.directive';
import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';

export type ChangePasswordFormType = FormGroup<{
    oldPassword: FormControl<string>;
    newPassword: FormControl<string>;
    newPasswordConfirm: FormControl<string>;
}>;

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        ReactiveFormsModule,
        TranslateModule,
        CardModule,
        PasswordModule,
        ButtonModule,
        AppErrorMessagesComponent
    ]
})
export class ChangePasswordComponent implements OnInit {
    public loading = false;
    public changePasswordForm: ChangePasswordFormType;
    public oldPasswordControl: FormControl<string>;
    public newPasswordControl: FormControl<string>;
    public newPasswordConfirmControl: FormControl;
    private changePasswordDTO: ChangePasswordDTO;

    constructor(
        private router: Router,
        private userAccountClient: UserAccountClient,
        private toastService: ToastService
    ) { }

    ngOnInit() {
        this.initializeForm();
    }

    hasValidPasswords(group: ChangePasswordFormType): boolean {
        const pass = group.value.newPassword;
        const confirmPass = group.value.newPasswordConfirm;

        if (pass !== confirmPass) {
            this.loading = false;
            this.toastService.showError(
                'i18n.core.messages.changePassword.title',
                'i18n.core.messages.changePassword.pwdDoNotMatch'
            );
            return false;
        }

        return true;
    }

    public changePassword(): void {
        this.loading = true;

        this.changePasswordDTO = new ChangePasswordDTO();
        this.changePasswordDTO.oldPassword = this.changePasswordForm.value.oldPassword;
        this.changePasswordDTO.newPassword = this.changePasswordForm.value.newPassword;

        if (!this.hasValidPasswords(this.changePasswordForm)) {
            return;
        }

        this.userAccountClient.changePassword(this.changePasswordDTO).subscribe({
            next: (result) => {
                this.loading = false;
                if (result) {
                    this.router.navigate([homeRoute]);
                }
            },
            error: error => {
                this.loading = false;
                this.toastService.showApiException(
                    error,
                    'i18n.core.messages.changePassword.title',
                    'i18n.core.messages.changePassword.message'
                );
            }
        });
    }

    private initializeForm(): void {
        this.oldPasswordControl = new FormControl('', [Validators.required]);
        this.newPasswordControl = new FormControl('', [Validators.required, passwordValidation()]);
        this.newPasswordConfirmControl = new FormControl('', [Validators.required, passwordValidation()]);

        this.changePasswordForm = new FormGroup({
            oldPassword: this.oldPasswordControl,
            newPassword: this.newPasswordControl,
            newPasswordConfirm: this.newPasswordConfirmControl
        });
    }
} 
